import React, { useState, useMemo } from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import SearchAndFilters from "./SearchAndFilters";
import PaginationControls from "./PaginationControls";
import SubDepartmentRow from "./SubDepartmentRow";
import Labels from "../../../labels/Labels";

export default function SubDepartmentList({
  subDepartments,
  isLoading,
  isError,
  selectedDepartment,
  showType,
  onShowStats,
  onShowMovements,
}) {
  const theme = useTheme(); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // Filter and search logic
  const filteredDepartments = useMemo(() => {
    if (!subDepartments) return [];

    let filtered = [...subDepartments];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((dept) =>
        dept.employerDescription.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply active/inactive filter
    if (filter === "active") {
      filtered = filtered.filter(
        (dept) =>
          dept.currentBalance > 0 ||
          dept.creditsThisMonth > 0 ||
          dept.debitsThisMonth > 0
      );
    } else if (filter === "inactive") {
      filtered = filtered.filter(
        (dept) =>
          dept.currentBalance === 0 &&
          dept.creditsThisMonth === 0 &&
          dept.debitsThisMonth === 0
      );
    }

    return filtered;
  }, [subDepartments, searchTerm, filter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);
  const paginatedDepartments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredDepartments.slice(startIndex, endIndex);
  }, [filteredDepartments, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (value) => {
    setFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography>{Labels.getLabel("queryBalance.LoadingSubdepartments", "Loading subdepartments...")}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">{Labels.getLabel("queryBalance.ErrorLoadingSubdepartments", "Error loading subdepartments")}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Title */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {Labels.getLabel("queryBalance.SubBalances", "Sub-Balances")}
        </Typography>

        {/* Search and Filters */}
        <SearchAndFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          filter={filter}
          onFilterChange={handleFilterChange}
        />

        {/* Table Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: 1.5,
            backgroundColor: theme.palette.primary.main,
            borderRadius: 1,
            marginBottom: 1,
          }}
        >
          <Box sx={{ width: "40px" }} /> {/* Space for expand icon */}
          <Typography
            variant="subtitle2"
            sx={{ flex: 1, color: "white", fontWeight: "bold" }}
          >
            {Labels.getLabel("queryBalance.SubBalance", "Sub-Balance")}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ width: "120px", textAlign: "right", color: "white", fontWeight: "bold" }}
          >
            {Labels.getLabel("queryBalance.Balance", "Balance")}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ width: "120px", textAlign: "right", color: "white", fontWeight: "bold" }}
          >
            {Labels.getLabel("queryBalance.CreditsM", "Credits (M)")}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ width: "120px", textAlign: "right", color: "white", fontWeight: "bold" }}
          >
            {Labels.getLabel("queryBalance.DebitsM", "Debits (M)")}
          </Typography>
          <Box sx={{ width: "80px" }} /> {/* Space for action icons */}
        </Box>

        {/* Department List */}
        {filteredDepartments.length === 0 ? (
          <Box sx={{ padding: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? `${Labels.getLabel("queryBalance.NoSubBalancesFoundMatching", "No sub-balances found matching")} "${searchTerm}"`
                : filter === "active"
                ? Labels.getLabel("queryBalance.NoActiveSubBalances", "No active sub-balances")
                : Labels.getLabel("queryBalance.NoInactiveSubBalances", "No inactive sub-balances")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box>
              {paginatedDepartments.map((dept) => (
                <SubDepartmentRow
                  key={dept.idEmployer}
                  department={dept}
                  isSelected={selectedDepartment === dept.idEmployer}
                  showType={showType}
                  onShowStats={onShowStats}
                  onShowMovements={onShowMovements}
                />
              ))}
            </Box>

            {/* Pagination */}
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredDepartments.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </>
        )}

        {/* Helper Text */}
        {filteredDepartments.length > 0 && (
          <Box sx={{ marginTop: 2, padding: 1, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <span style={{ fontSize: "14px" }}>▶</span> = {Labels.getLabel("queryBalance.ClickToExpand", "Click to expand")} |
              <span style={{ fontSize: "14px" }}>▼</span> = {Labels.getLabel("queryBalance.Expanded", "Expanded")}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
